/**
 * 深海領域システム - 定数
 *
 * 水属性専用メカニクス「深海潜伏」に関する定数定義
 */

import type { SurfaceBonus } from './types';

// ========================================
// 基本定数
// ========================================

/** 深海領域のデフォルト上限 */
export const DEFAULT_DEEP_SEA_CAPACITY = 3;

/** フィールドカードによる拡張上限 */
export const EXTENDED_DEEP_SEA_CAPACITY = 5;

/** 浮上コストの最低値 */
export const MIN_SURFACE_COST = 1;

/** 深度ボーナスの上限（これ以上の深度でも効果は変わらない） */
export const MAX_DEPTH_BONUS = 5;

/** 潜航時のデフォルト初期深度 */
export const DEFAULT_INITIAL_DEPTH = 1;

/** ヴェルゼファールの初期深度ボーナス */
export const VELZEFAHL_INITIAL_DEPTH_BONUS = 2;

// ========================================
// 深度ボーナステーブル
// ========================================

/**
 * 深度に応じた浮上ボーナス
 *
 * | 深度 | ATKボーナス | 先制攻撃 | 浮上時効果 |
 * |------|------------|----------|-----------|
 * | 1    | 0          | No       | No        |
 * | 2    | +200       | No       | No        |
 * | 3    | +400       | Yes      | No        |
 * | 4    | +600       | Yes      | No        |
 * | 5+   | +800       | Yes      | Yes       |
 */
export const DEPTH_BONUS_TABLE: Record<number, SurfaceBonus> = {
  1: { attackBonus: 0, canPreemptiveStrike: false, triggerSurfaceEffect: false },
  2: { attackBonus: 200, canPreemptiveStrike: false, triggerSurfaceEffect: false },
  3: { attackBonus: 400, canPreemptiveStrike: true, triggerSurfaceEffect: false },
  4: { attackBonus: 600, canPreemptiveStrike: true, triggerSurfaceEffect: false },
  5: { attackBonus: 800, canPreemptiveStrike: true, triggerSurfaceEffect: true },
};

/**
 * 深度ボーナスを取得（深度5以上は同じ）
 */
export function getDepthBonus(depth: number): SurfaceBonus {
  if (depth <= 0) {
    return { attackBonus: 0, canPreemptiveStrike: false, triggerSurfaceEffect: false };
  }
  const effectiveDepth = Math.min(depth, MAX_DEPTH_BONUS);
  return DEPTH_BONUS_TABLE[effectiveDepth] || DEPTH_BONUS_TABLE[1];
}

// ========================================
// 潜航可能条件
// ========================================

/** 潜航可能な属性 */
export const SUBMERGIBLE_ATTRIBUTES = ['水'] as const;
export type SubmergibleAttribute = typeof SUBMERGIBLE_ATTRIBUTES[number];

/** 潜航可能なカテゴリ（【潜航】キーワードがなくてもフェイズカード効果で潜航可能） */
export const SUBMERGIBLE_CATEGORIES = [
  '【ヴェルゼファール】',
  '【リヴァイアサン】',
  '【ディープシャドウ】',
  '【マーメイド】',
] as const;

// ========================================
// カードID定数
// ========================================

/** 深海領域を開放するカードID */
export const DEEP_SEA_UNLOCK_CARDS = {
  /** フェイズカード: 荒海の玉座・アビスロード */
  PHASE_ABYSS_LORD: 'C0000521',
  /** フィールドカード: 荒海の海溝・アビストレンチ */
  FIELD_ABYSS_TRENCH: 'C0000533',
} as const;

/** 深海系モンスターカードID */
export const DEEP_SEA_MONSTER_CARDS = {
  /** 荒海の斥候・スカウトフィン */
  SCOUT_FIN: 'C0000522',
  /** 荒海の待ち伏せ・アンブッシャー */
  AMBUSHER: 'C0000523',
  /** 荒海の番人・アビスガード */
  ABYSS_GUARD: 'C0000524',
  /** 荒海の導き手・タイドコーラー */
  TIDE_CALLER: 'C0000525',
  /** 荒海王の眷属・レヴィアタン */
  LEVIATHAN: 'C0000526',
  /** 荒海王ヴェルゼファール */
  VELZEFAHL: 'C0000527',
} as const;

/** 深海系魔法カードID */
export const DEEP_SEA_MAGIC_CARDS = {
  /** 深海への招き */
  INVITATION: 'C0000528',
  /** 海底からの呼び声 */
  CALL_FROM_DEPTHS: 'C0000529',
  /** 深度加速 */
  DEPTH_ACCELERATION: 'C0000530',
  /** 荒海の強襲 */
  ABYSS_ASSAULT: 'C0000531',
  /** レヴィアタンの大時化 */
  LEVIATHAN_STORM: 'C0000532',
} as const;

/** 深海対策カードID（原始属性） */
export const DEEP_SEA_COUNTER_CARDS = {
  /** 干潟の呪術師 */
  TIDAL_SHAMAN: 'C0000534',
  /** 大干ばつ */
  GREAT_DROUGHT: 'C0000535',
  /** 浅瀬に誘う者 */
  SHALLOW_LURE: 'C0000536',
  /** 干上がる海 */
  DRYING_SEA: 'C0000537',
  /** 待ち伏せる大蛇 */
  AMBUSH_SERPENT: 'C0000538',
  /** アビスイータードラゴン */
  ABYSS_EATER_DRAGON: 'C0000539',
} as const;

/** 全深海系カードID一覧 */
export const ALL_DEEP_SEA_CARD_IDS = [
  ...Object.values(DEEP_SEA_UNLOCK_CARDS),
  ...Object.values(DEEP_SEA_MONSTER_CARDS),
  ...Object.values(DEEP_SEA_MAGIC_CARDS),
  ...Object.values(DEEP_SEA_COUNTER_CARDS),
] as const;

// ========================================
// 【深淵の牙】関連
// ========================================

/** 【深淵の牙】発動条件深度 */
export const SHINEN_NO_KIBA_MIN_DEPTH = 3;

/** 【深淵の牙】ダメージ係数（深度×この値） */
export const SHINEN_NO_KIBA_DAMAGE_MULTIPLIER = 200;

/**
 * 【深淵の牙】のダメージを計算
 */
export function calculateShinenNoKibaDamage(depth: number): number {
  return depth * SHINEN_NO_KIBA_DAMAGE_MULTIPLIER;
}

// ========================================
// 【海底の眠り】関連
// ========================================

/** 【海底の眠り】回復量（ターン終了時） */
export const KAITEI_NO_NEMURI_HEAL_AMOUNT = 200;

// ========================================
// 浮上コスト計算
// ========================================

/**
 * 浮上コストを計算
 * @param baseCost カードの元コスト
 * @param depth 現在の深度
 * @param costReduction 追加のコスト軽減（ヴェルゼファール等）
 * @returns 必要なSPコスト（最低1）
 */
export function calculateSurfaceCost(
  baseCost: number,
  depth: number,
  costReduction: number = 0
): number {
  const reducedCost = baseCost - depth - costReduction;
  return Math.max(MIN_SURFACE_COST, reducedCost);
}

// ========================================
// 初期状態
// ========================================

/**
 * 深海領域の初期状態を作成
 */
export function createInitialDeepSeaZone() {
  return {
    cards: [],
    maxCapacity: DEFAULT_DEEP_SEA_CAPACITY,
    isUnlocked: false,
  };
}
